import styled from "styled-components";

import { formatDate } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import type { IDurationObject } from "app/services/DurationService";
import { FlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { TooltipTitle } from "../table/bubble-chart/chart/tooltip/BubbleChartTooltipTitle";

const StripedBulletWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const StripedBullet = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: repeating-linear-gradient(
        -45deg,
        ${(props) => props.color},
        ${(props) => props.color} 2px,
        transparent 2px,
        transparent 4px
    );
`;

interface TooltipRow {
    period: { label: string; backgroundColor: string; displayDashedBullet: boolean };
    value: { value: string };
    change: { value: string | null };
}

const periodColumn: ColumnDefinition<TooltipRow> = {
    key: "period",
    get title() {
        return translate("Period");
    },
    selector: "period" as const,
    align: "left",
    size: "2fr",
    render: ({ period }) => {
        const { label, backgroundColor, displayDashedBullet } = period;
        if (displayDashedBullet) {
            return (
                <FlexRow gap={"8px"}>
                    <StripedBulletWrapper>
                        <StripedBullet color={backgroundColor} />
                    </StripedBulletWrapper>
                    <ChartTooltip.LabelCell label={label} />
                </FlexRow>
            );
        }
        return (
            <FlexColumn gap={"4px"}>
                <ChartTooltip.LabelCell
                    label={label}
                    backgroundColor={backgroundColor}
                />
            </FlexColumn>
        );
    },
};

const valueColumn: ColumnDefinition<TooltipRow> = {
    key: "value",
    title: "Value",
    selector: "value" as const,
    align: "right",
    size: "1fr",
    borderRight: true,
    render: ({ value }) => {
        return (
            <ChartTooltip.ValueCell
                value={value.value}
                data-automation="market-shares-chart-tooltip-value"
            />
        );
    },
};

const changeColumn: ColumnDefinition<TooltipRow> = {
    key: "change",
    get title() {
        return translate("common.pop.change");
    },
    selector: "change" as const,
    align: "right",
    size: "1fr",
    render: ({ change }) => {
        const { value } = change;
        if (value === null) {
            return null;
        }

        return (
            <ChartTooltip.ChangeCell
                value={value}
                isDecrease={Number(value.replace(/[^0-9.-]/g, "")) < 0}
                // @ts-expect-error FIXME: value is string but compared to number 0 — pre-existing behavior
                unsigned={value === 0 || value === "-"}
            />
        );
    },
};

const getColumnsDefinition = () => {
    return [periodColumn, valueColumn, changeColumn];
};

const getData = (
    currentValue: number,
    comparedValue: number,
    valueChange: string,
    color: string,
    filters: IDurationObject<string>,
    labelFormatter: (y: number) => string,
) => {
    return [
        {
            period: {
                label: formatDate(filters.from, filters.to),
                backgroundColor: color,
                displayDashedBullet: false,
            },
            value: {
                value: labelFormatter(currentValue),
            },
            change: {
                value: comparedValue === 0 ? "-" : valueChange,
            },
        },
        {
            period: {
                label: formatDate(filters.compareFrom, filters.compareTo),
                backgroundColor: color,
                displayDashedBullet: true,
            },
            value: {
                value: comparedValue === 0 ? "-" : labelFormatter(comparedValue),
            },
            change: {
                value: null,
            },
        },
    ];
};

interface Props {
    websiteTitle: string;
    websiteIcon?: string;
    subDisplayText?: string;
    currentValue: number;
    comparedValue: number;
    valueChange: string;
    color?: string;
    filters: IDurationObject<string>;
    labelFormatter: (y: number) => string;
}

export const MarketSharesPeriodOverPeriodTooltipFormatter = ({
    websiteTitle,
    websiteIcon,
    subDisplayText,
    currentValue,
    comparedValue,
    valueChange,
    color = colorsPalettes.green[400],
    filters,
    labelFormatter,
}: Props) => {
    return (
        <ChartTooltip
            width={370}
            title={
                <TooltipTitle
                    entityDisplayName={websiteTitle}
                    favicon={websiteIcon}
                    subtitle={subDisplayText}
                />
            }
            columnDefinition={getColumnsDefinition()}
            data={getData(currentValue, comparedValue, valueChange, color, filters, labelFormatter)}
        />
    );
};
